import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, Loader2, Pencil, Save, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useDocumentos } from "@/components/DocumentoEditable";

export const AiReport = ({ eventId, text, onDone, canEdit, stale, huella }) => {
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState("");
  // la orden retocada por el operador se guarda aparte (el plan se recalcula con cada cambio) y se muestra en su lugar
  const { docs, guardar, quitar } = useDocumentos(eventId);
  const editada = docs.orden;
  const [borrador, setBorrador] = useState(null);  // null = no se está editando
  const [guardando, setGuardando] = useState(false);

  const run = async () => {
    if (editada && !window.confirm("Hay una versión editada a mano de la orden de servicio. Si la vuelve a redactar, se perderá. ¿Continuar?")) return;
    setBusy(true);
    setLive("");
    let acc = "";
    try {
      const res = await fetch(`${API}/events/${eventId}/report`, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "No se pudo generar el informe");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = JSON.parse(p.slice(6));
          if (d.error) throw new Error(d.error);
          if (d.t) { acc += d.t; setLive(acc); }
        }
      }
      onDone(acc);
      if (editada) await quitar("orden").catch(() => {});
      toast.success("Orden de servicio redactada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
      setLive("");
    }
  };

  const shown = busy ? live : editada?.texto ?? text;
  const guardarBorrador = async () => {
    setGuardando(true);
    try { await guardar("orden", { texto: borrador }); setBorrador(null); toast.success("Orden de servicio guardada con sus cambios"); }
    catch (e) { toast.error(errMsg(e)); } finally { setGuardando(false); }
  };
  const restaurar = async () => {
    if (!window.confirm("Se perderán los cambios hechos a mano y se mostrará la orden redactada por la IA. ¿Continuar?")) return;
    try { await quitar("orden"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="ai-report-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500 max-w-xl">La IA redacta la orden de servicio a partir de los cálculos del plan: cronología, cortes, desvíos, relevos de personal y señalización.</p>
        {canEdit && borrador === null && (
          <div className="flex flex-wrap gap-2">
            {shown && !busy && <Button variant="outline" onClick={() => setBorrador(shown)} data-testid="editar-orden-button"><Pencil className="w-4 h-4 mr-2" /> Editar</Button>}
            <Button onClick={run} disabled={busy || stale} data-testid="generate-ai-report-button" className="bg-amber-500 hover:bg-amber-400 text-slate-950">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {text || editada ? "Volver a redactar" : "Redactar orden de servicio"}
            </Button>
          </div>
        )}
      </div>
      {editada && borrador === null && !busy && (
        <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2 text-sm mb-3 ${huella && editada.huella && editada.huella !== huella ? "border-red-600 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="orden-editada">
          <span className="flex-1 min-w-[14rem]">Versión editada a mano · {editada.updated_by}, {new Date(editada.updated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}.
            {huella && editada.huella && editada.huella !== huella && <b> El plan ha cambiado después de editarla: revísela.</b>}</span>
          {canEdit && <button onClick={restaurar} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-current/30 text-xs font-semibold" data-testid="restaurar-orden"><RotateCcw className="w-3.5 h-3.5" /> Volver a la redactada por la IA</button>}
        </div>
      )}
      {borrador !== null && (
        <div className="space-y-2 mb-3" data-testid="orden-editor">
          <div className="text-xs text-slate-500">Formato sencillo: «# Título», «## Apartado», «- punto», «**negrita**» y tablas con «|».</div>
          <textarea value={borrador} onChange={(e) => setBorrador(e.target.value)} className="w-full h-[60vh] rounded-xl border border-slate-300 p-4 font-mono text-[13px] leading-relaxed focus:outline-none focus:border-sky-500" data-testid="orden-textarea" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBorrador(null)} disabled={guardando} data-testid="orden-cancelar"><X className="w-4 h-4 mr-2" /> Cancelar</Button>
            <Button onClick={guardarBorrador} disabled={guardando || !borrador.trim()} className="bg-emerald-600 hover:bg-emerald-500" data-testid="orden-guardar">{guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar</Button>
          </div>
        </div>
      )}
      {stale && <div className="text-xs text-amber-700 mb-3">Regenere el plan antes de redactar el informe.</div>}
      {borrador !== null ? null : shown ? (
        <div className="report-md bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="ai-report-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{shown}</ReactMarkdown>
        </div>
      ) : (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-10 text-center" data-testid="ai-report-empty">Aún no se ha redactado el informe.</div>
      )}
    </div>
  );
};
